import { doc, getDoc, setDoc, deleteDoc, collection, getDocs, QuerySnapshot, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import axios from 'axios';
import * as jose from 'jose';
import { OnboardingItem, DashboardMetrics, PLMSMetrics } from '../types/plms';

const BASE_URL = 'https://sheets.googleapis.com/v4/spreadsheets';

// JWT Authentication (same pattern as sheetsService.ts)
let cachedToken: string | null = null;
let tokenExpiry: number | null = null;

const PRIVATE_KEY = process.env.REACT_APP_GOOGLE_SHEETS_PRIVATE_KEY?.replace(/\\n/g, '\n');
const CLIENT_EMAIL = process.env.REACT_APP_GOOGLE_SHEETS_CLIENT_EMAIL;

// Create a JWT token (same as sheetsService.ts)
const createJWT = async (): Promise<{ token: string; expiresIn: number }> => {
  if (!PRIVATE_KEY || !CLIENT_EMAIL) {
    throw new Error('Missing service account credentials');
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = 3600; // 1 hour
  const expiry = now + expiresIn;

  const privateKey = await jose.importPKCS8(PRIVATE_KEY, 'RS256');
  const jwt = await new jose.SignJWT({
    scope: 'https://www.googleapis.com/auth/spreadsheets'
  })
    .setProtectedHeader({ alg: 'RS256', typ: 'JWT' })
    .setIssuedAt(now)
    .setIssuer(CLIENT_EMAIL)
    .setSubject(CLIENT_EMAIL)
    .setAudience('https://sheets.googleapis.com/')
    .setExpirationTime(expiry)
    .sign(privateKey);

  return { token: jwt, expiresIn };
};

// Get a valid token, using cache if available (same as sheetsService.ts)
const getAuthToken = async (): Promise<string> => {
  if (cachedToken && tokenExpiry && Date.now() < tokenExpiry) {
    return cachedToken;
  }

  const { token, expiresIn } = await createJWT();
  cachedToken = token;
  tokenExpiry = Date.now() + (expiresIn * 1000);
  return token;
};

export const getWorkbookId = async (email: string): Promise<string | null> => {
  const docRef = doc(db, 'plms_users', email);
  const docSnap: DocumentSnapshot = await getDoc(docRef);
  return docSnap.exists() ? docSnap.data().workbookId : null;
};

export const getAllWorkbookMappings = async (): Promise<{ email: string; workbookId: string }[]> => {
  const querySnapshot: QuerySnapshot = await getDocs(collection(db, 'plms_users'));
  return querySnapshot.docs.map(doc => ({
    email: doc.id,
    workbookId: doc.data().workbookId
  }));
};

export const assignWorkbook = async (email: string, workbookId: string): Promise<void> => {
  await setDoc(doc(db, 'plms_users', email), { workbookId });
};

export const removeWorkbook = async (email: string): Promise<void> => {
  await deleteDoc(doc(db, 'plms_users', email));
};

export const fetchAllOnboardingItems = async (
  workbookId: string,
  filters: {
    section?: string;
    day?: number;
    completionStatus?: 'all' | 'completed' | 'notCompleted';
    includeMasterLists?: boolean;
  } = {}
): Promise<OnboardingItem[]> => {
  const authToken = await getAuthToken();
  if (!authToken) throw new Error('No auth token available');

  try {
    // Get completion data from SOPs/Onboarding Items
    const sopsRange = encodeURIComponent('SOPs/Onboarding Items!P4:X1000');
    const sopsResponse = await axios.get(
      `${BASE_URL}/${workbookId}/values/${sopsRange}`,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    // Get URLs from All Onboarding Items
    const urlsRange = encodeURIComponent('All Onboarding Items!A3:E1000');
    const urlsResponse = await axios.get(
      `${BASE_URL}/${workbookId}/values/${urlsRange}`,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    if (!sopsResponse.data.values) {
      return [];
    }

    // Create a map of item text to URL from All Onboarding Items
    const urlMap = new Map();
    if (urlsResponse.data.values) {
      urlsResponse.data.values.forEach((row: any[]) => {
        if (row[3]) {
          const itemText = row[3].trim().toLowerCase().replace(/\s+/g, ' ');
          const url = row[4]?.trim() || '';
          if (itemText) {
            urlMap.set(itemText, url);
          }
        }
      });
    }

    // Process items from SOPs/Onboarding Items with URLs from the map
    let items = sopsResponse.data.values.map((row: any[], index: number): OnboardingItem => {
      const rawItemText = row[3]?.trim() || '';
      const normalizedItemText = rawItemText.toLowerCase().replace(/\s+/g, ' ');
      
      const item = {
        rowNumber: index + 4,
        section: row[0]?.trim() || '',
        day: row[2] ? parseInt(row[2]) : 0,
        item: rawItemText,
        note: row[5]?.trim() || '',
        dateDone: row[7]?.trim() || '',
        personalNotes: row[8]?.trim() || '',
        url: urlMap.get(normalizedItemText) || ''
      };
      return item;
    });

    // Filter out Day 0 items unless specifically requested
    if (!filters.includeMasterLists) {
      items = items.filter((item: OnboardingItem) => item.day > 0);
    }

    // Apply filters and sort
    let filteredItems = items;

    if (filters.section) {
      filteredItems = filteredItems.filter((item: OnboardingItem) => item.section === filters.section);
    }

    if (filters.day !== undefined) {
      filteredItems = filteredItems.filter((item: OnboardingItem) => item.day === filters.day);
    }

    if (filters.completionStatus) {
      switch (filters.completionStatus) {
        case 'completed':
          filteredItems = filteredItems.filter((item: OnboardingItem) => !!item.dateDone);
          break;
        case 'notCompleted':
          filteredItems = filteredItems.filter((item: OnboardingItem) => !item.dateDone);
          break;
      }
    }

    // Sort by day number
    filteredItems.sort((a: OnboardingItem, b: OnboardingItem) => a.day - b.day);

    return filteredItems;
  } catch (error) {
    console.error('Error fetching all onboarding items:', error);
    throw error;
  }
};

export const fetchOnboardingItems = async (
  workbookId: string,
  page: number = 0,
  filters: {
    section?: string;
    day?: number;
    completionStatus?: 'all' | 'completed' | 'notCompleted';
    includeMasterLists?: boolean;
  } = {}
): Promise<OnboardingItem[]> => {
  // First get all items and apply filters
  const allItems = await fetchAllOnboardingItems(workbookId, filters);
  
  // Filter out Day 0 items unless specifically requested
  let filteredItems = filters.includeMasterLists 
    ? allItems 
    : allItems.filter((item: OnboardingItem) => item.day > 0);

  // Apply section filter if provided
  if (filters.section) {
    filteredItems = filteredItems.filter((item: OnboardingItem) => item.section === filters.section);
  }

  // Apply day filter if provided
  if (filters.day !== undefined) {
    filteredItems = filteredItems.filter((item: OnboardingItem) => item.day === filters.day);
  }

  // Apply completion status filter
  if (filters.completionStatus) {
    switch (filters.completionStatus) {
      case 'completed':
        filteredItems = filteredItems.filter((item: OnboardingItem) => !!item.dateDone);
        break;
      case 'notCompleted':
        filteredItems = filteredItems.filter((item: OnboardingItem) => !item.dateDone);
        break;
    }
  }

  // Sort by day number
  filteredItems.sort((a: OnboardingItem, b: OnboardingItem) => a.day - b.day);

  // Apply pagination
  const start = page * 20;
  const end = start + 20;
  return filteredItems.slice(start, end);
};

export const fetchDashboardMetrics = async (workbookId: string): Promise<DashboardMetrics> => {
  const authToken = await getAuthToken();
  
  try {
    // Get name from Sections/Categories sheet
    const nameRange = encodeURIComponent("'Sections/Categories'!A4");
    const nameResponse = await axios.get(
      `${BASE_URL}/${workbookId}/values/${nameRange}`,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    // Get dashboard metrics
    const dashboardRange = encodeURIComponent('Dashboard!A1:B20');
    const dashboardResponse = await axios.get(
      `${BASE_URL}/${workbookId}/values/${dashboardRange}`,
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    if (!dashboardResponse.data.values) {
      throw new Error('Invalid response format from Google Sheets');
    }

    const values = dashboardResponse.data.values;
    const metrics: { [key: string]: any } = {};
    
    values.forEach(([key, value]: [string, any]) => {
      if (key) {
        metrics[key.trim()] = value;
      }
    });

    // Ensure startDate is a valid date string
    let startDate = metrics['Start Date'];
    if (startDate) {
      try {
        // Try to parse the date and format it as ISO string
        startDate = new Date(startDate).toISOString();
      } catch (e) {
        console.warn('Invalid start date:', startDate);
        startDate = new Date().toISOString(); // Fallback to current date
      }
    } else {
      startDate = new Date().toISOString(); // Default to current date
    }

    return {
      name: nameResponse.data.values?.[0]?.[0] || '',
      startDate,
      doneItems: parseInt(metrics['Done Items'] || '0'),
      itemsRemaining: parseInt(metrics['Items Remaining'] || '0'),
      percentDone: parseFloat(metrics['% Done'] || '0'),
      itemsDoneLastWeek: parseInt(metrics['Items Done Last 7 Days'] || '0'),
      shouldBeOnDay: parseInt(metrics['Should Be On Day'] || '0'),
      actuallyOnDay: parseInt(metrics['Actually on Day'] || '0'),
      daysLeft: parseInt(metrics['Days Left'] || '0'),
      lastItemDoneDaysAgo: parseInt(metrics['Last Item Done Days Ago'] || '0'),
      latestDoneItem: metrics['Latest Done Item'] || '',
      toBePasted: parseInt(metrics['To Be Pasted'] || '0')
    };
  } catch (error) {
    console.error('Error fetching dashboard metrics:', error);
    throw error;
  }
};

export const convertToPLMSMetrics = (metrics: DashboardMetrics): PLMSMetrics => {
  return {
    doneItems: metrics.doneItems,
    itemsRemaining: metrics.itemsRemaining,
    percentDone: metrics.percentDone,
    itemsDoneLastWeek: metrics.itemsDoneLastWeek,
    lastActivity: metrics.latestDoneItem,
  };
};

export const markItemComplete = async (
  workbookId: string,
  rowNumber: number,
  date: string
): Promise<void> => {
  const authToken: string = await getAuthToken();
  const range = encodeURIComponent(`SOPs/Onboarding Items!W${rowNumber}`);

  try {
    const response = await axios.put(
      `${BASE_URL}/${workbookId}/values/${range}?valueInputOption=RAW`,
      {
        values: [[date]]
      },
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    if (response.status !== 200) {
      throw new Error('Failed to mark item complete');
    }
  } catch (error: any) {
    console.error('Error marking item complete:', error);
    throw error;
  }
};

export const updatePersonalNote = async (
  workbookId: string,
  rowNumber: number,
  note: string
): Promise<void> => {
  const authToken: string = await getAuthToken();
  const range = encodeURIComponent(`SOPs/Onboarding Items!V${rowNumber}`);

  try {
    const response = await axios.put(
      `${BASE_URL}/${workbookId}/values/${range}?valueInputOption=RAW`,
      {
        values: [[note]]
      },
      {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        }
      }
    );

    if (response.status !== 200) {
      throw new Error('Failed to update personal note');
    }
  } catch (error: any) {
    console.error('=== Detailed Error Information ===');
    console.error('Error:', error.message);
    
    if (error.response) {
      console.error('Response Status:', error.response.status);
      console.error('Response Status Text:', error.response.statusText);
      console.error('Response Headers:', JSON.stringify(error.response.headers, null, 2));
      console.error('Response Data:', JSON.stringify(error.response.data, null, 2));
    } else if (error.request) {
      console.error('No response received. Request:', error.request);
    } else {
      console.error('Error setting up request:', error.message);
    }
    console.error('=== End Error Information ===');
    
    throw error;
  }
};

// Re-export cache functions and types
export { 
  getCachedData, 
  getCachedFilterOptions,
  invalidateCacheOnComplete 
} from './plmsCache';
export type { PLMSFilters } from './plmsCache';
